/**
 * Sparse-save wire diff (#148 step 2).
 *
 * A stale full-form save reverts every field the session doesn't know about,
 * because the server treats absence as deletion. The fix is to send only what
 * the user actually changed: at load we capture a flattened snapshot of the
 * arrangement exactly as the wire pipeline (formAsPostData → arraysToObjects →
 * JSON) would send it, and at save we diff the current wire payload against
 * that snapshot.
 *
 * Everything here operates on the JSON wire shape — payloads are round-tripped
 * through JSON.stringify/parse before flattening so the diff describes exactly
 * the bytes the server would receive (undefined dropped, Dates stringified),
 * and flattening mirrors the server's dot-object semantics (dotted leaf paths;
 * empty objects/arrays kept as leaves).
 */

import _ from 'lodash';
import { FormStructure } from '../forms/form-structure';
import { addPathKeyMarkersForPaths } from '../forms/path-key-markers';

export interface SparseDiff {
  /** Dotted leaf path → new value, for paths added or changed since the snapshot. */
  changed: { [path: string]: any };
  /** Dotted leaf paths present in the snapshot but absent from the current payload. */
  deleted: string[];
}

export type FlatWireData = { [path: string]: any };

/**
 * Dot-flatten a wire payload. Matches the server's dot.dot() semantics:
 * plain objects and arrays recurse into dotted/indexed keys; primitives,
 * null, and EMPTY objects/arrays are leaves.
 */
export function flattenWire(value: any, prefix: string = '', out: FlatWireData = {}): FlatWireData {
  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value);

    if (keys.length === 0) {
      if (prefix) {
        out[prefix] = Array.isArray(value) ? [] : {};
      }
      return out;
    }

    for (const key of keys) {
      flattenWire(value[key], prefix ? prefix + '.' + key : key, out);
    }

    return out;
  }

  if (prefix) {
    out[prefix] = value;
  }

  return out;
}

/**
 * Capture a snapshot of a save payload in wire form: JSON round-trip (so the
 * snapshot holds what would actually go over HTTP), then flatten.
 */
export function captureWireSnapshot(payload: any): FlatWireData {
  return flattenWire(JSON.parse(JSON.stringify(payload)));
}

/** Leaves are primitives or empty objects/arrays (see flattenWire). */
function leafEqual(a: any, b: any): boolean {
  if (a === b) {
    return true;
  }

  // Empty-container leaves: [] === [] and {} === {} by value.
  if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
    return Array.isArray(a) === Array.isArray(b);
  }

  return false;
}

/**
 * Diff the current wire payload against the load-time snapshot.
 * `changed` = new or different leaves; `deleted` = leaves that disappeared.
 */
export function diffWire(snapshot: FlatWireData, current: FlatWireData): SparseDiff {
  const changed: { [path: string]: any } = {};
  const deleted: string[] = [];

  for (const path of Object.keys(current)) {
    if (!(path in snapshot) || !leafEqual(snapshot[path], current[path])) {
      changed[path] = current[path];
    }
  }

  for (const path of Object.keys(snapshot)) {
    if (!(path in current)) {
      deleted.push(path);
    }
  }

  return { changed, deleted };
}

/** True when a diff carries nothing — a save with no user edits. */
export function isEmptySparseDiff(diff: SparseDiff): boolean {
  return Object.keys(diff.changed).length === 0 && diff.deleted.length === 0;
}

/**
 * Drop phantom "changed: null" entries for paths the snapshot doesn't have.
 * Forms materialise empty controls as null; when such a form value is written
 * into the payload (BDM settings, facility modals, provider forms), the diff
 * reports nothing → null for every empty field. Those are no-ops — the server
 * also refuses to create rows from them — so strip them client-side too. A
 * null over a path that EXISTS in the snapshot survives: that's a real clear.
 */
export function stripPhantomNullCreates(diff: SparseDiff, snapshot: FlatWireData): SparseDiff {
  for (const path of Object.keys(diff.changed)) {
    if (diff.changed[path] === null && !(path in snapshot)) {
      delete diff.changed[path];
    }
  }
  return diff;
}

/**
 * Attach the `__pathKey` markers relevant to a sparse diff (incident 2026-07-13,
 * reworked for #238).
 *
 * Rows in arrangement_data are inserted with pathKey = NULL and only tagged
 * afterwards by the server's backFillPathKeys, driven by `<path>__pathKey`
 * markers in the payload. A sparse diff carries only the leaves that changed,
 * so the marker for each one has to be added here or the replacement row is
 * stored untagged and every pathKey-dependent read (dashboard sync, External
 * API jurisdiction, locations export) silently loses it.
 *
 * The markers are resolved from the form STRUCTURE, not harvested from the
 * form: FormGenerator used to add a marker FormControl beside each tagged leaf
 * and any `FormGroup.reset()` nulled it, which is how ~half of new
 * arrangements lost their region tag. The structure is the one declaration of
 * which paths are tagged, so it is the one place to read them from. Deleted
 * paths need no markers (their rows are soft-deleted, not re-inserted).
 */
export function attachPathKeyMarkers(diff: SparseDiff, structure: FormStructure): SparseDiff {
  const changed = addPathKeyMarkersForPaths(Object.keys(diff.changed), structure, {
    ...diff.changed,
  });

  return { changed, deleted: diff.deleted };
}

/**
 * The full-payload counterpart of attachPathKeyMarkers: a copy of a nested
 * wire payload with a `<path>__pathKey` sibling added next to every node the
 * structure tags. Used for full saves, the shadow-mode payload and the
 * create() PATCH, which send the whole form rather than a diff.
 */
export function withPathKeyMarkers(payload: any, structure: FormStructure): any {
  // The JSON round-trip is the copy: it is exactly the shape HttpClient sends
  // (undefined dropped, Dates as ISO strings), so marking it changes no bytes
  // and avoids a second deep clone.
  const marked = JSON.parse(JSON.stringify(payload));
  const markers = addPathKeyMarkersForPaths(Object.keys(flattenWire(marked)), structure, {});

  for (const key of Object.keys(markers)) {
    // The marker's parent is a prefix of a real wire path, so it already
    // exists in the payload; _.set only adds the sibling key. An array path,
    // not a string: lodash would read '[' or ']' inside an instance id as an
    // index.
    _.set(marked, key.split('.'), markers[key]);
  }

  return marked;
}
